
<script setup>
import { computed } from "vue";
import Text from "@/components/common/Text.vue";
import {useI18n} from "vue-i18n";

const { t } = useI18n();
const props = defineProps({
    items: {
        type: Array,
        required: true
    },

    groupKey: {
        type: String,
        required: true
    },

    titleKey: {
        type: String,
        required: true
    },

    groupIcon: {
        type: String,
        default: "mdi-folder"
    },

    connectionIcon: {
        type: String,
        default: "mdi-access-point-network"
    }
});
const emit = defineEmits(["view"]);

function openDetails(item) {
    emit("view", item);
}

const grouped = computed(() => {
    const map = {};
    const DEFAULT_GROUP = t("common.labels.individualProducts");

    const comboItem = props.items.find(
        item => item.product?.st_combo === true
    );

    const hasCombo = !!comboItem;

    props.items.forEach(item => {
        let groupKey;
        if (hasCombo) {
            groupKey = "COMBO_GROUP";
        } else {
            groupKey = item[props.groupKey] ?? DEFAULT_GROUP;
        }

        if (!map[groupKey]) {
            map[groupKey] = {
                key: groupKey,
                comboParent: null,
                connection: item.isp_clientAccess_id ?? item.isp_clientAccess,
                children: []
            };
        }

        const product = item.product;

        if (product?.st_combo === true) {
            map[groupKey].comboParent = product;
            return;
        }

        if (product) {
            map[groupKey].children.push(product);
        }
    });
    return Object.values(map).map(group => {
        const isCombo = !!group.comboParent;

        return {
            group: isCombo
                ? group.comboParent.name
                : group.key,
            url: isCombo
                ? `/products/view/${group.comboParent.id}`
                : null,
            isCombo,
            children: group.children,
            connection: group.connection
        };
    });
});

</script>

<template>
    <v-expansion-panels multiple>
        <v-expansion-panel
            v-for="group in grouped"
            :key="group.group"
        >
            <v-expansion-panel-title>
                <strong>{{ group.group }}</strong>
                <v-btn
                    v-if="group.isCombo"
                    icon
                    :title="t('common.labels.viewCombo')"
                    variant="text"
                    @click.stop="openDetails(group.url)"
                >
                    <v-icon >mdi-eye</v-icon>
                </v-btn>
            </v-expansion-panel-title>

            <v-expansion-panel-text>
                <v-list >
                    <v-list-item
                        v-for="(child, index) in group.children"
                        :key="index"
                        class="mt-2"
                        @click="$emit('view', child)"
                        :title="child[titleKey]"
                    >
                        <template #append>
                            <v-btn
                                icon
                                :title="t('common.labels.viewProducts')"
                                variant="text"
                                @click.stop="openDetails('/contracts/products/view/'+child.id)"
                            >
                                <v-icon >mdi-eye</v-icon>
                            </v-btn>
                        </template>
                        <Text v-if="group.connection" >
                            {{ t("common.labels.connection")}} {{ group?.connection?.username }}
                            <v-btn
                                icon
                                variant="text"
                                :title="t('common.labels.viewConnection')"
                                @click.stop="openDetails('/contracts/connections/view/'+ group.connection.id)"
                            >
                                <v-icon class="text-grey">mdi-eye</v-icon>
                            </v-btn>
                        </Text>
                        <Text v-else color="grey">
                            Não faz parte de uma conexão
                        </Text>
                    </v-list-item>
                </v-list>
            </v-expansion-panel-text>
        </v-expansion-panel>
    </v-expansion-panels>
</template>

<style scoped>
.v-list {
    padding: 0 !important;
}
div.v-expansion-panel-text__wrapper {
    padding-top: 0 !important;
}
</style>
